import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Radio,
  Plug,
  PlugZap,
  QrCode,
  Bot,
  AlertTriangle,
  Flame,
  Trash2,
  GraduationCap,
  FlaskConical,
  Bell,
  BellOff,
  Megaphone,
  Eye,
  UserCircle2,
} from 'lucide-react';
import { hasRole, type Channel } from '@conversia/shared-types';
import {
  puedeConfirmar,
  textoDeAccion,
  exigeEscribirNombre,
  type AccionPeligrosa,
} from '@/lib/acciones-peligrosas';
import * as channelsApi from '@/api/channels.api';
import * as agentsApi from '@/api/agents.api';
import * as trainingApi from '@/api/training.api';
import * as usersApi from '@/api/users.api';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { BotonEliminar } from '@/components/ui/BotonEliminar';
import { MenuAcciones } from '@/components/jobs/MenuAcciones';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/common/PageHeader';
import { ChannelConnectModal } from '@/components/channel/ChannelConnectModal';
import { QrModal } from '@/components/channel/QrModal';
import { ApiError } from '@/api/client';
import { channelStatusBadge, channelTypeLabel, channelWarmupLabel } from '@/lib/badges';
import { useChannelsStore } from '@/store/channels.store';
import { useToast } from '@/hooks/useToast';
import { Cargando } from '@/components/common/Cargando';
import { Toggle } from '@/components/ui/Toggle';
import { useAuthStore } from '@/store/auth.store';
import { CONFIRMACION_APAGAR_AVISOS, estadoDeAvisos } from '@/lib/avisos-canal';

export default function Channels() {
  const qc = useQueryClient();
  const toast = useToast();
  const setStatus = useChannelsStore((s) => s.setStatus);
  const [connectOpen, setConnectOpen] = useState(false);
  // Freno de las acciones que dejan al negocio sin WhatsApp (ver acciones-peligrosas).
  const [peligro, setPeligro] = useState<{ accion: AccionPeligrosa; canal: Channel } | null>(null);
  const [confirmacionPeligro, setConfirmacionPeligro] = useState('');
  const [qrChannel, setQrChannel] = useState<Channel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Channel | null>(null);
  // CANDADO DE SIMULACRO. Quitarlo pide escribir el nombre del canal: desde ese
  // momento salen mensajes REALES. Ponerlo no pide nada: frenar es seguro.
  const [simulacroTarget, setSimulacroTarget] = useState<Channel | null>(null);
  const [confirmacionSimulacro, setConfirmacionSimulacro] = useState('');

  // AVISOS AUTOMÁTICOS POR CANAL (solo admin). Apagar pide confirmación porque
  // corta recordatorios, llegada, encuestas y avisos internos de los clientes
  // del canal; encender no pide nada.
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role ? hasRole(role, 'ADMIN') : false;
  const [avisosTarget, setAvisosTarget] = useState<Channel | null>(null);
  const cambiarAvisos = useMutation({
    mutationFn: (v: { id: string; pausados: boolean }) =>
      channelsApi.setAvisosPausados(v.id, v.pausados),
    onSuccess: (r) => {
      toast.success(
        r.avisosPausados
          ? 'Avisos automáticos apagados para los clientes de este canal'
          : 'Avisos automáticos encendidos de nuevo',
      );
      setAvisosTarget(null);
      void qc.invalidateQueries({ queryKey: ['channels', 'list'] });
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : 'No se pudieron cambiar los avisos del canal',
      ),
  });

  // Canal de los avisos que no son de un cliente (resumen de las 8 PM, alertas).
  const { data: generales } = useQuery({
    queryKey: ['channels', 'avisos-generales'],
    queryFn: () => channelsApi.getAvisosGenerales(),
    enabled: isAdmin,
  });
  const usarParaGenerales = useMutation({
    mutationFn: (id: string) => channelsApi.setAvisosGenerales(id),
    onSuccess: () => {
      toast.success('Listo: los avisos generales salen por este canal');
      void qc.invalidateQueries({ queryKey: ['channels', 'avisos-generales'] });
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : 'No se pudo elegir el canal de avisos generales',
      ),
  });
  const simulacro = useMutation({
    mutationFn: (v: { id: string; quitar: boolean; confirmacion?: string }) =>
      v.quitar
        ? channelsApi.quitarSimulacro(v.id, v.confirmacion ?? '')
        : channelsApi.ponerSimulacro(v.id),
    onSuccess: (r) => {
      toast.success(
        r.simulacro
          ? 'Candado puesto: este canal ya no manda nada'
          : 'Candado quitado: este canal manda mensajes REALES',
      );
      setSimulacroTarget(null);
      setConfirmacionSimulacro('');
      void qc.invalidateQueries({ queryKey: ['channels', 'list'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const { data: channels, isLoading } = useQuery({
    queryKey: ['channels', 'list'],
    queryFn: () => channelsApi.listChannels(),
  });

  // Agentes ACTIVE para el selector de "agente por defecto" del canal (Bug #3).
  const { data: agents } = useQuery({
    queryKey: ['agents', 'list', 'ACTIVE'],
    queryFn: () => agentsApi.listAgents({ status: 'ACTIVE', take: 200 }),
  });

  // Para decir de quién es una línea de vigilancia.
  const { data: usuarios } = useQuery({
    queryKey: ['users', 'list', 'para-vigilancia'],
    queryFn: () => usersApi.listUsers({ take: 200 }),
  });

  const vigilancia = useMutation({
    mutationFn: (v: { id: string; soloVigilancia: boolean; vigilaUserId?: string | null }) =>
      channelsApi.marcarVigilancia(v.id, v.soloVigilancia, v.vigilaUserId),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      toast.info(
        r.soloVigilancia
          ? 'Línea en modo vigilancia: se lee, nunca se escribe'
          : r.vigilaUserId
            ? 'Guardado: esta línea tiene dueño y atiende a sus clientes'
            : 'Guardado: línea sin dueño, sus clientes se reparten por turno',
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setDefaultAgent = useMutation({
    mutationFn: ({ id, defaultAgentId }: { id: string; defaultAgentId: string | null }) =>
      channelsApi.updateChannel(id, { defaultAgentId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.info('Agente por defecto actualizado');
    },
    onError: () => toast.error('No se pudo actualizar el agente'),
  });

  const connect = useMutation({
    mutationFn: (channel: Channel) => channelsApi.connectChannel(channel.id),
    onSuccess: (result, channel) => {
      setStatus(channel.id, result.status, result.qr);
      void qc.invalidateQueries({ queryKey: ['channels'] });
      setQrChannel(channel);
    },
    // El servidor explica por qué no se pudo (ambiente de prueba, canal archivado…):
    // tragarse ese motivo dejaba al usuario sin saber qué hacer.
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'No se pudo iniciar la conexión'),
  });

  const disconnect = useMutation({
    mutationFn: (id: string) => channelsApi.disconnectChannel(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.info('Canal desconectado');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'No se pudo desconectar'),
  });

  // Modo entrenamiento a nivel canal: el agente calla en TODO el canal y las
  // respuestas humanas se capturan como ejemplos (tanda sala-maquinas).
  const channelTraining = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => trainingApi.setChannelTraining(id, on),
    onSuccess: (_r, { on }) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.success(
        on
          ? 'Canal en modo entrenamiento: el agente no responde en este canal'
          : 'Entrenamiento del canal terminado: el agente vuelve a responder',
      );
    },
    onError: () => toast.error('No se pudo cambiar el modo entrenamiento del canal'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => channelsApi.deleteChannel(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      toast.success('Canal eliminado');
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'No se pudo eliminar el canal'),
  });

  const items = channels ?? [];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Canales"
        helpKey="channels"
        description="Conecta WhatsApp y otras plataformas para recibir conversaciones."
        action={
          <Button data-guia="channels.conectar" onClick={() => setConnectOpen(true)}>
            <Plus className="h-4 w-4" /> Conectar canal
          </Button>
        }
      />

      {isLoading ? (
        <Cargando tamano="pantalla" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Radio}
          title="No hay canales conectados"
          description="Conecta tu primer canal de WhatsApp para empezar a recibir mensajes."
          action={
            <Button data-guia="channels.conectar" onClick={() => setConnectOpen(true)}>
              <Plus className="h-4 w-4" /> Conectar canal
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((channel) => {
            const badge = channelStatusBadge(channel.status);
            const isConnected = channel.status === 'CONNECTED';
            const warmup = channelWarmupLabel(channel);
            const avisos = estadoDeAvisos(channel);
            const esCanalGeneral = generales?.efectivoId === channel.id;
            return (
              <Card key={channel.id}>
                <CardBody className="flex flex-col gap-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        <Radio className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                          {channel.name}
                        </h3>
                        <p className="text-xs text-slate-400">{channelTypeLabel[channel.type]}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                      {warmup && !warmup.done && (
                        <Badge variant="warning" title={warmup.tooltip}>
                          <Flame className="h-3 w-3" /> {warmup.badge}
                        </Badge>
                      )}
                      {channel.trainingMode && <Badge variant="warning">Entrenando</Badge>}
                      {channel.soloVigilancia && (
                        <Badge
                          variant="info"
                          title="Se lee para saber si el vendedor atendió a sus clientes. No envía nada y la asistente no responde acá."
                        >
                          <Eye className="h-3 w-3" /> Solo vigilancia
                        </Badge>
                      )}
                      {channel.simulacro && (
                        <Badge
                          variant="info"
                          title="El canal NO manda nada: cada envío queda registrado, pero no sale"
                        >
                          <FlaskConical className="h-3 w-3" /> Simulacro
                        </Badge>
                      )}
                      {!avisos.encendidos && (
                        <Badge
                          variant="warning"
                          title="No sale nada automático para los clientes de este canal"
                        >
                          <BellOff className="h-3 w-3" /> Sin avisos
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 border-t border-surface-border pt-3 dark:border-slate-800">
                    {isConnected ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setConfirmacionPeligro('');
                          setPeligro({ accion: 'desconectar', canal: channel });
                        }}
                        loading={disconnect.isPending && disconnect.variables === channel.id}
                      >
                        <Plug className="h-4 w-4" /> Desconectar
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => connect.mutate(channel)}
                        loading={connect.isPending && connect.variables?.id === channel.id}
                      >
                        <PlugZap className="h-4 w-4" /> Conectar
                      </Button>
                    )}
                    {!isConnected && (
                      <Button variant="ghost" size="sm" onClick={() => setQrChannel(channel)}>
                        <QrCode className="h-4 w-4" /> Ver QR
                      </Button>
                    )}
                    <Button
                      variant={channel.trainingMode ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => {
                        // Terminar el entrenamiento devuelve la voz a la asistente: eso no
                        // deja a nadie sin atender, así que no lleva freno.
                        if (channel.trainingMode) {
                          channelTraining.mutate({ id: channel.id, on: false });
                          return;
                        }
                        setConfirmacionPeligro('');
                        setPeligro({ accion: 'entrenar', canal: channel });
                      }}
                      loading={
                        channelTraining.isPending && channelTraining.variables?.id === channel.id
                      }
                      title={
                        channel.trainingMode
                          ? 'Terminar el entrenamiento del canal'
                          : 'Modo entrenamiento: el agente calla en todo el canal'
                      }
                    >
                      <GraduationCap className="h-4 w-4" />
                      {channel.trainingMode ? 'Terminar' : 'Entrenar'}
                    </Button>
                    {!channel.simulacro && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => simulacro.mutate({ id: channel.id, quitar: false })}
                        loading={simulacro.isPending && simulacro.variables?.id === channel.id}
                        title="Poner el candado: el canal deja de mandar mensajes y solo los registra"
                      >
                        <FlaskConical className="h-4 w-4" /> Simulacro
                      </Button>
                    )}
                    {/*
                      Las dos acciones que no se deshacen con un clic, fuera de la
                      fila principal (Esteban, 21-sep-2026). «Activar envíos» va en
                      el menú ⋯: no se busca por error, pero está. Borrar, en el
                      zafacón rojo. Las dos siguen abriendo su confirmación —la de
                      Activar envíos pide escribir el nombre del canal—.
                    */}
                    <div className="ml-auto flex items-center gap-1">
                      {channel.simulacro && (
                        <MenuAcciones
                          etiqueta="Más acciones del canal"
                          acciones={[
                            {
                              label: 'Activar envíos',
                              icono: <FlaskConical className="h-4 w-4" />,
                              ayuda: 'Quita el candado: los mensajes salen de verdad',
                              onClick: () => setSimulacroTarget(channel),
                            },
                          ]}
                        />
                      )}
                      <BotonEliminar
                        etiqueta={`Eliminar el canal ${channel.name}`}
                        onClick={() => {
                        setConfirmacionPeligro('');
                        setPeligro({ accion: 'eliminar', canal: channel });
                      }}
                      />
                    </div>
                  </div>

                  {channel.lastError && !isConnected && (
                    <div className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{channel.lastError}</span>
                    </div>
                  )}

                  {isAdmin && channel.type === 'WHATSAPP' && (
                    <div className="flex flex-col gap-2 border-t border-surface-border pt-3 text-xs dark:border-slate-800">
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 font-medium text-slate-600 dark:text-slate-300">
                          {avisos.encendidos ? (
                            <Bell className="h-3.5 w-3.5" />
                          ) : (
                            <BellOff className="h-3.5 w-3.5 text-amber-600" />
                          )}
                          {avisos.etiqueta}
                        </span>
                        <Toggle
                          checked={avisos.encendidos}
                          label={`Avisos automáticos del canal ${channel.name}`}
                          disabled={
                            cambiarAvisos.isPending && cambiarAvisos.variables?.id === channel.id
                          }
                          onChange={(encender) =>
                            encender
                              ? cambiarAvisos.mutate({ id: channel.id, pausados: false })
                              : setAvisosTarget(channel)
                          }
                        />
                      </div>
                      {avisos.detalle && (
                        <p className="text-amber-700 dark:text-amber-300">{avisos.detalle}</p>
                      )}
                      {esCanalGeneral ? (
                        <Badge
                          variant="info"
                          title="Por este canal salen los avisos que no son de un cliente: el resumen de las 8 PM y las alertas del sistema"
                        >
                          <Megaphone className="h-3 w-3" /> Canal de avisos generales
                          {generales?.configuradoId ? '' : ' (automático)'}
                        </Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="self-start"
                          onClick={() => usarParaGenerales.mutate(channel.id)}
                          loading={
                            usarParaGenerales.isPending &&
                            usarParaGenerales.variables === channel.id
                          }
                          title="Los avisos que no son de un cliente (resumen de las 8 PM, alertas) saldrán por este canal"
                        >
                          <Megaphone className="h-4 w-4" /> Usar para avisos generales
                        </Button>
                      )}
                    </div>
                  )}

                  {/*
                    SOLO VIGILANCIA · el WhatsApp propio de un vendedor.
                    El sistema lo lee para saber si le escribió a sus clientes;
                    no envía nada por ahí y la asistente no responde. Solo entra
                    lo que sea con un cliente de la tienda: lo personal ni se
                    guarda.
                  */}
                  {/*
                    DOS COSAS DISTINTAS, DOS CONTROLES.

                    Acá había UN desplegable para las dos, y encima mentía:
                    mostraba «No — esta línea atiende clientes» siempre que la
                    línea no fuera de solo vigilancia, AUNQUE TUVIERA DUEÑO. Las
                    líneas de Eyevis, Pedro y Yoheiri se veían sin dueño
                    teniéndolo, así que tocar el desplegable se lo borraba sin
                    que se viera en ninguna parte.

                    Pasó el 21-oct-2026 a las 12:17: los tres perdieron el dueño,
                    sus clientes volvieron a repartirse por turno y dos fueron al
                    vendedor equivocado. Y escoger un dueño encendía la vigilancia
                    de paso, que es lo que dejó muda la línea de las fichas.
                  */}
                  {isAdmin && (
                    <div className="flex flex-col gap-3 border-t border-surface-border pt-3 text-xs dark:border-slate-800">
                      <label className="flex flex-col gap-1">
                        <span className="flex items-center gap-1.5 font-medium text-slate-500 dark:text-slate-400">
                          <UserCircle2 className="h-3.5 w-3.5" /> De quién es esta línea
                        </span>
                        <select
                          value={channel.vigilaUserId ?? ''}
                          disabled={vigilancia.isPending}
                          onChange={(e) =>
                            vigilancia.mutate({
                              id: channel.id,
                              // La vigilancia NO se toca al cambiar el dueño.
                              soloVigilancia: channel.soloVigilancia ?? false,
                              vigilaUserId: e.target.value || null,
                            })
                          }
                          className="rounded-lg border border-surface-border bg-surface px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900"
                        >
                          <option value="">De nadie — los clientes se reparten por turno</option>
                          {(usuarios?.items ?? []).map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name ?? u.email}
                            </option>
                          ))}
                        </select>
                        <span className="text-[11px] text-slate-400">
                          El cliente que escriba acá se le asigna al dueño, y el dueño es el único
                          que ve estos clientes en el panel.
                        </span>
                      </label>

                      <label className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={channel.soloVigilancia ?? false}
                          disabled={vigilancia.isPending || !channel.vigilaUserId}
                          onChange={(e) =>
                            // El dueño NO va en el envío: así el servidor lo deja
                            // como está en vez de borrarlo.
                            vigilancia.mutate({ id: channel.id, soloVigilancia: e.target.checked })
                          }
                          className="mt-0.5 h-3.5 w-3.5 rounded border-surface-border"
                        />
                        <span>
                          <span className="flex items-center gap-1.5 font-medium text-slate-500 dark:text-slate-400">
                            <Eye className="h-3.5 w-3.5" /> Solo se lee, nunca se escribe
                          </span>
                          <span className="mt-0.5 block text-[11px] text-slate-400">
                            {channel.vigilaUserId
                              ? 'Para mirar el WhatsApp de un vendedor: la asistente no responde acá y esta línea no envía NADA.'
                              : 'Primero hay que decir de quién es la línea.'}
                          </span>
                        </span>
                      </label>
                    </div>
                  )}

                  <label className="flex flex-col gap-1 border-t border-surface-border pt-3 text-xs dark:border-slate-800">
                    <span className="flex items-center gap-1.5 font-medium text-slate-500 dark:text-slate-400">
                      <Bot className="h-3.5 w-3.5" /> Agente por defecto
                    </span>
                    <select
                      value={channel.defaultAgentId ?? ''}
                      disabled={setDefaultAgent.isPending}
                      onChange={(e) =>
                        setDefaultAgent.mutate({
                          id: channel.id,
                          defaultAgentId: e.target.value || null,
                        })
                      }
                      className="h-9 w-full rounded-md border border-surface-border bg-white px-2 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    >
                      <option value="">Sin agente (responde un humano)</option>
                      {agents?.items.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <ChannelConnectModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        onCreated={(channel) => connect.mutate(channel)}
      />
      <QrModal channel={qrChannel} open={Boolean(qrChannel)} onClose={() => setQrChannel(null)} />

      <Modal
        open={simulacroTarget != null}
        onClose={() => {
          setSimulacroTarget(null);
          setConfirmacionSimulacro('');
        }}
        title="Activar envíos reales"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setSimulacroTarget(null);
                setConfirmacionSimulacro('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              disabled={confirmacionSimulacro.trim() !== (simulacroTarget?.name ?? '').trim()}
              loading={simulacro.isPending}
              onClick={() =>
                simulacroTarget &&
                simulacro.mutate({
                  id: simulacroTarget.id,
                  quitar: true,
                  confirmacion: confirmacionSimulacro,
                })
              }
            >
              Activar envíos reales
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Hoy <strong>{simulacroTarget?.name}</strong> no le manda nada a nadie: cada mensaje queda
          registrado pero no sale. Desde que lo actives,{' '}
          <strong>los mensajes salen de verdad a teléfonos de clientes reales</strong>.
        </p>
        <div className="mt-3">
          <Input
            label={`Para confirmar, escribe el nombre del canal: ${simulacroTarget?.name ?? ''}`}
            value={confirmacionSimulacro}
            autoFocus
            onChange={(e) => setConfirmacionSimulacro(e.target.value)}
          />
        </div>
      </Modal>
      <Modal
        open={avisosTarget != null}
        onClose={() => !cambiarAvisos.isPending && setAvisosTarget(null)}
        title="Apagar avisos automáticos"
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setAvisosTarget(null)}
              disabled={cambiarAvisos.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={cambiarAvisos.isPending}
              onClick={() =>
                avisosTarget && cambiarAvisos.mutate({ id: avisosTarget.id, pausados: true })
              }
            >
              <BellOff className="h-4 w-4" /> Apagar avisos
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <span className="font-semibold">{avisosTarget?.name}</span>: {CONFIRMACION_APAGAR_AVISOS}
        </p>
      </Modal>
      <Modal
        open={peligro != null}
        onClose={() => {
          setPeligro(null);
          setConfirmacionPeligro('');
        }}
        title={peligro ? textoDeAccion(peligro.accion, peligro.canal.name).titulo : ''}
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setPeligro(null);
                setConfirmacionPeligro('');
              }}
            >
              Mejor no
            </Button>
            <Button
              variant="danger"
              disabled={
                !peligro || !puedeConfirmar(role, peligro.canal.name, confirmacionPeligro)
              }
              loading={disconnect.isPending || channelTraining.isPending || remove.isPending}
              onClick={() => {
                if (!peligro) return;
                const { accion, canal } = peligro;
                if (accion === 'desconectar') disconnect.mutate(canal.id);
                if (accion === 'entrenar') channelTraining.mutate({ id: canal.id, on: true });
                if (accion === 'eliminar') remove.mutate(canal.id);
                setPeligro(null);
                setConfirmacionPeligro('');
              }}
            >
              {peligro ? textoDeAccion(peligro.accion, peligro.canal.name).botón : ''}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700 dark:text-slate-200">
          {peligro ? textoDeAccion(peligro.accion, peligro.canal.name).advertencia : ''}
        </p>
        {peligro && exigeEscribirNombre(role) && (
          <div className="mt-3">
            <Input
              label={`Para confirmar, escribe el nombre del canal: ${peligro.canal.name}`}
              value={confirmacionPeligro}
              autoFocus
              onChange={(e) => setConfirmacionPeligro(e.target.value)}
            />
          </div>
        )}
      </Modal>
      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => !remove.isPending && setDeleteTarget(null)}
        title="Eliminar canal"
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={remove.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
            >
              <Trash2 className="h-4 w-4" /> Eliminar
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          ¿Eliminar canal <span className="font-semibold">{deleteTarget?.name}</span>? Esta acción
          no se puede deshacer. Las conversaciones y mensajes asociados se mantendrán como
          histórico.
        </p>
      </Modal>
    </div>
  );
}
